import get from 'lodash/get.js';
import { ACTIVITY_TYPES } from '@/constants/ActivityTypes.js';
import Activity from '@/models/Activity.js';
import AttemptAuthorization from '@/models/AttemptAuthorization.js';
import Course from '@/models/Course.js';
import Model from '@/models/Model.js';
import { isPastDue } from '@/utilities/activityPastDue.js';
import { isDiscussionWeekRestrictionActive } from '@/utilities/discussionWeekRestriction.js';

/**
 * ActivityStatus Model
 *
 * Vuex ORM model for managing activity status flags.
 * This model stores computed status information for activities
 * to centralize status banner logic.
 */
export default class ActivityStatus extends Model {
    static entity = 'activity_statuses';

    static modelName = 'ActivityStatus';

    static primaryKey = 'activity_id';

    static fields() {
        return {
            // Primary key is the activity_id
            activity_id: this.attr(null),

            // Activity type for type-specific logic
            activity_type: this.attr(null),

            // File count for file-submission activities (tracked locally)
            file_count: this.number(0),

            // Live discussion counts, refreshed by `verifyActivity` after each post so completion
            // updates without a full page reload. Null until synced → isCompleted falls back to the
            // activity's own (load-time) activity_content counts.
            discussion_threads: this.attr(null),
            discussion_replies: this.attr(null),

            // Computed status flags (not persisted to backend)
            _computed_statuses: this.attr({}),

            ...super.fields(),

            // Relationship to the actual activity
            activity: this.belongsTo(Activity, 'activity_id'),
        };
    }

    /**
     * Get the activity instance (memoized per ActivityStatus instance to avoid repeated ORM queries per render).
     */
    get activityData() {
        if (!this._cachedActivity || this._cachedActivity.id !== this.activity_id) {
            this._cachedActivity = Activity.query()
                .with(['activity_settings', 'activity_content'])
                .find(this.activity_id);
        }

        return this._cachedActivity;
    }

    resolveActivity(reactiveActivity = null) {
        const baseActivity = this.activityData;

        if (!reactiveActivity) {
            return baseActivity;
        }

        if (!baseActivity) {
            return reactiveActivity;
        }

        // Merge ORM baseline with reactive overlay so partial component state
        // (e.g. quiz attempt flags) does not mask activity settings from the store.
        return { ...baseActivity, ...reactiveActivity };
    }

    getActivityType(activity, reactiveActivity = null) {
        return (
            activity?.activity_type ||
            activity?.activity_type_key ||
            reactiveActivity?.activity_type ||
            reactiveActivity?.activity_type_key ||
            this.activity_type ||
            ''
        );
    }

    getIsCompletedForActivity(activity, activityType = null) {
        if (!activity) {
            return false;
        }

        const type = activityType || this.getActivityType(activity);

        if (type === ACTIVITY_TYPES.DISCUSSION_THREAD) {
            // Prefer the live counts synced by `verifyActivity` (updated after each post); fall back
            // to the activity's activity_content, which only refreshes on a full page load. This is
            // why completion previously required a manual refresh to appear.
            const threads = this.discussion_threads ?? get(activity, 'activity_content.threads', 0) ?? 0;
            const replies = this.discussion_replies ?? get(activity, 'activity_content.replies', 0) ?? 0;
            const minThreads = get(activity, 'activity_content.min_threads', 0) || 0;
            const minReplies = get(activity, 'activity_content.min_replies', 0) || 0;

            // Mirror the backend (`VerifyDiscussionActivity`: `passes() ? latestAction() : null`):
            // meeting the minimums is not enough — with no minimums configured (0/null) and zero
            // posts, `0 >= 0 && 0 >= 0` would wrongly read as complete. Require actual participation,
            // matching the backend's `latestAction()` guard (null when the learner has not posted).
            const meetsMinimums = threads >= minThreads && replies >= minReplies;
            const hasParticipation = threads > 0 || replies > 0;

            return meetsMinimums && hasParticipation;
        }

        // For all other types, check the completed field from the Activity model.
        // The API appends `completed` as a boolean, but the optimistic store update written when a
        // learner marks an activity complete sets it to a timestamp string. Treat any truthy value
        // as completed so the corporate "Next" button appears immediately on completion (not only
        // after a reload), matching how the rest of the app reads `activity.completed`.
        return Boolean(activity.completed);
    }

    getIsLateForActivity(activity) {
        return isPastDue(activity?.activity_settings?.due_at);
    }

    getCanSubmitLateForActivity(activity) {
        return get(activity, 'activity_settings.allow_submissions_after_due_date', false);
    }

    getTraditionalAttemptsRemainingForActivity(activity) {
        if (!activity) {
            return Number.POSITIVE_INFINITY;
        }

        if (this.getIsLateForActivity(activity) && !this.getCanSubmitLateForActivity(activity)) {
            return 0;
        }

        const maxAttempts = get(activity, 'activity_settings.max_attempts_allowed', 0);

        if (maxAttempts === 0) {
            return Number.POSITIVE_INFINITY;
        }

        const submissionCount = get(activity, 'user_submission_count', 0);

        return Math.max(0, maxAttempts - submissionCount);
    }

    getTotalAttemptsRemainingForActivity(authUser, filterToUserId, activity) {
        return (
            this.getTraditionalAttemptsRemainingForActivity(activity) +
            this.getAuthorizedAttemptsRemaining(authUser, filterToUserId)
        );
    }

    getIsUserOutOfAttemptsForActivity(authUser, filterToUserId, activity) {
        if (!activity?.activity_settings || !authUser) {
            return false;
        }

        if (authUser.is_admin || authUser.is_instructor) {
            return false;
        }

        const maxAttempts = get(activity, 'activity_settings.max_attempts_allowed', 0);

        if (maxAttempts === 0) {
            return false;
        }

        if (this.getIsLateForActivity(activity) && !this.getCanSubmitLateForActivity(activity)) {
            return true;
        }

        return this.getTotalAttemptsRemainingForActivity(authUser, filterToUserId, activity) <= 0;
    }

    hasUserSubmittedFileForActivity(authUser, activity) {
        if (!activity || !authUser) {
            return false;
        }

        return get(activity, 'user_submission_count', 0) > 0;
    }

    /**
     * Check if activity is completed
     * Matches the logic from Activity.vue computed property
     */
    get isCompleted() {
        return this.getIsCompletedForActivity(this.activityData);
    }

    /**
     * Check if activity is past due date
     */
    get isLate() {
        return this.getIsLateForActivity(this.activityData);
    }

    /**
     * Check if late submissions are allowed
     */
    get canSubmitLate() {
        return this.getCanSubmitLateForActivity(this.activityData);
    }

    /**
     * Get all attempt authorizations for this activity
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations
     * @returns {Array} Array of attempt authorizations
     */
    getAttemptAuthorizations(filterToUserId = null) {
        const query = AttemptAuthorization.query().where('activity_id', this.activity_id);

        if (filterToUserId) {
            query.where('user_id', filterToUserId);
        }

        return query.get();
    }

    /**
     * Check if activity has any attempt authorizations
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations
     * @returns {boolean}
     */
    hasAttemptAuthorizations(filterToUserId = null) {
        return this.getAttemptAuthorizations(filterToUserId).length > 0;
    }

    /**
     * Get user's unused attempt authorizations
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {Array} Array of unused attempt authorizations
     */
    getUserUnusedAuthorizedAttempts(authUser, filterToUserId = null) {
        if (!authUser) {
            return [];
        }

        return this.getAttemptAuthorizations(filterToUserId)
            .filter(auth => auth.user_id === authUser.id && auth.used_at === null)
            .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
    }

    /**
     * Check if user has unused authorized attempt
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {boolean}
     */
    userHasAuthorizedAttempt(authUser, filterToUserId = null) {
        return this.getUserUnusedAuthorizedAttempts(authUser, filterToUserId).length > 0;
    }

    /**
     * Check if user is out of attempts (for file submission)
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {boolean}
     */
    isUserOutOfAttempts(authUser, filterToUserId = null) {
        return this.getIsUserOutOfAttemptsForActivity(authUser, filterToUserId, this.activityData);
    }

    // ==================== Discussion Thread Specific ====================

    /**
     * Check if thread limit is reached
     */
    get threadLimitReached() {
        const activity = this.activityData;
        if (!activity?.activity_content) {
            return false;
        }

        const maxThreads = activity.activity_content?.max_threads ?? null;
        const threads = activity.activity_content?.threads ?? 0;

        // Treat 0 or null max_threads as "no limit"
        if (!maxThreads) {
            return false;
        }

        return threads >= maxThreads;
    }

    /**
     * Check if user can create discussion thread
     * Mirrors the logic from DiscussionThreadPolicy.create()
     * @param {Object} authUser - The authenticated user object
     * @param {Object|null} reactiveActivity - Activity from the page (Activities getter); preferred over ORM alone
     * @returns {boolean}
     */
    canUserCreateDiscussionThread(authUser, reactiveActivity = null) {
        const activity = reactiveActivity || this.activityData;
        if (!activity?.course_id || !authUser) {
            return false;
        }

        let course = Course.find(activity.course_id);
        if (!course) {
            const store = typeof this.store === 'function' ? this.store() : null;
            const fromStore = store?.getters?.['Courses/get']?.(activity.course_id);
            course = fromStore ?? null;
        }
        if (!course) {
            return false;
        }

        // Check if user has the "create a discussion thread" permission
        // This mirrors the DiscussionThreadPolicy.create() logic
        const hasPermissionViaCourse = authUser.hasPermissionViaCourseTo(course, 'create a discussion thread');

        const hasPermissionViaGroup = authUser.hasGroupPermissionWithinCourse(course, 'create a discussion thread');

        return hasPermissionViaCourse || hasPermissionViaGroup;
    }

    getDiscussionRestrictedUntilWeekStart(authUser, reactiveActivity = null) {
        if (!authUser) return false;

        const activity = reactiveActivity || this.activityData;
        const courseId = activity?.course_id;
        if (!courseId) return false;

        if (typeof authUser.isInstructorOf === 'function' && authUser.isInstructorOf(courseId)) {
            return false;
        }
        if (typeof authUser.isGroupInstructorOf === 'function' && authUser.isGroupInstructorOf(courseId)) {
            return false;
        }

        const enrollment = typeof authUser.enrollmentFor === 'function' ? authUser.enrollmentFor(courseId) : null;
        const rolesOnEnrollment = Array.isArray(enrollment?.roles) ? enrollment.roles : [];
        const isStudentOrTAFromCourseEnrollment = rolesOnEnrollment.some(
            role => role?.name === 'Student' || role?.name === 'Teaching Assistant'
        );

        const store = typeof this.store === 'function' ? this.store() : null;
        const activeEnrollmentRoles = store?.getters?.['ActiveEnrollment/roles'];
        const isStudentOrTAFromActiveEnrollment = Array.isArray(activeEnrollmentRoles)
            ? activeEnrollmentRoles.some(role => role?.name === 'Student' || role?.name === 'Teaching Assistant')
            : false;

        const hasRole = store?.getters?.['User/hasRole'];
        const isStudentOrTAFromUserModule =
            typeof hasRole === 'function' && (hasRole('Student') || hasRole('Teaching Assistant'));

        const isStudentOfCourse = typeof authUser.isStudentOf === 'function' && authUser.isStudentOf(courseId);

        if (
            !isStudentOrTAFromCourseEnrollment &&
            !isStudentOrTAFromActiveEnrollment &&
            !isStudentOrTAFromUserModule &&
            !isStudentOfCourse
        ) {
            return false;
        }

        return isDiscussionWeekRestrictionActive(get(activity, 'activity_content'));
    }

    // ==================== File Submission Specific ====================

    /**
     * Get minimum files required
     */
    get minFiles() {
        const activity = this.activityData;
        return get(activity, 'activity_content.min_files', 0);
    }

    /**
     * Get maximum files allowed
     */
    get maxFiles() {
        const activity = this.activityData;
        return get(activity, 'activity_content.max_files', 0);
    }

    /**
     * Check if minimum files requirement not met
     */
    get minFilesNotMet() {
        return this.file_count < this.minFiles;
    }

    /**
     * Check if maximum files exceeded
     */
    get maxFilesExceeded() {
        return this.maxFiles > 0 && this.file_count > this.maxFiles;
    }

    /**
     * Check if file validation should be shown
     */
    get showFileValidation() {
        return this.minFiles > 0 && !this.isCompleted;
    }

    /**
     * Get file validation status object
     */
    get fileValidationStatus() {
        return {
            minFiles: this.minFiles,
            maxFiles: this.maxFiles,
            fileCount: this.file_count,
            minFilesNotMet: this.minFilesNotMet,
            maxFilesExceeded: this.maxFilesExceeded,
            showFileValidation: this.showFileValidation,
        };
    }

    // ==================== Quiz/Survey Specific ====================

    /**
     * Check if activity is past due (alias for isLate for quiz/survey compatibility)
     */
    get isPastDue() {
        return this.isLate;
    }

    /**
     * Get traditional attempts remaining
     */
    get traditionalAttemptsRemaining() {
        return this.getTraditionalAttemptsRemainingForActivity(this.activityData);
    }

    /**
     * Get authorized attempts remaining
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {number}
     */
    getAuthorizedAttemptsRemaining(authUser, filterToUserId = null) {
        if (!authUser) {
            return 0;
        }

        return this.getAttemptAuthorizations(filterToUserId).filter(
            auth => auth.user_id === authUser.id && auth.used_at === null
        ).length;
    }

    /**
     * Get total remaining attempts (traditional + authorized)
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {number}
     */
    getTotalAttemptsRemaining(authUser, filterToUserId = null) {
        return this.getTotalAttemptsRemainingForActivity(authUser, filterToUserId, this.activityData);
    }

    /**
     * Check if max quiz attempts reached
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @returns {boolean}
     */
    isMaxReached(authUser, filterToUserId = null) {
        return this.getTotalAttemptsRemaining(authUser, filterToUserId) <= 0;
    }

    // ==================== Consolidated Status Getter ====================

    /**
     * Check if user has submitted a file
     * @param {Object} authUser - The authenticated user object
     * @returns {boolean}
     */
    hasUserSubmittedFile(authUser) {
        return this.hasUserSubmittedFileForActivity(authUser, this.activityData);
    }

    /**
     * Check if user has any attempt authorizations (used or unused)
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations
     * @returns {boolean}
     */
    userHasAnyAttemptAuthorization(authUser, filterToUserId = null) {
        if (!authUser) {
            return false;
        }

        const authorizations = this.getAttemptAuthorizations(filterToUserId);
        return authorizations.some(auth => auth.user_id === authUser.id);
    }

    /**
     * Get all statuses for this activity
     * Returns an object with all relevant status flags based on activity type
     *
     * @param {Object} authUser - The authenticated user object
     * @param {number|null} filterToUserId - Optional user ID to filter authorizations (for permission checks)
     * @param {Object} reactiveActivity - Optional reactive activity object from component prop
     * @returns {Object} Object with all relevant status flags
     */
    getAllStatuses(authUser, filterToUserId = null, reactiveActivity = null) {
        const activity = this.resolveActivity(reactiveActivity);
        const activityTypeForStatuses = this.getActivityType(activity, reactiveActivity);

        // Shared statuses derived from the reactive page activity when available
        const sharedStatuses = {
            isCompleted: this.getIsCompletedForActivity(activity, activityTypeForStatuses),
            isLate: this.getIsLateForActivity(activity),
            isPastDue: this.getIsLateForActivity(activity),
            canSubmitLate: this.getCanSubmitLateForActivity(activity),
            hasAttemptAuthorizations: this.hasAttemptAuthorizations(filterToUserId),
            userHasAuthorizedAttempt: this.userHasAuthorizedAttempt(authUser, filterToUserId),
            userUnusedAuthorizedAttempts: this.getUserUnusedAuthorizedAttempts(authUser, filterToUserId),
            outOfAttempts: this.getIsUserOutOfAttemptsForActivity(authUser, filterToUserId, activity),
            hasSubmittedFile: this.hasUserSubmittedFileForActivity(authUser, activity),
            userHasAnyAttemptAuthorization: this.userHasAnyAttemptAuthorization(authUser, filterToUserId),
        };

        // Add type-specific statuses
        switch (activityTypeForStatuses) {
            case ACTIVITY_TYPES.DISCUSSION_THREAD:
                return {
                    ...sharedStatuses,
                    threadLimitReached: this.threadLimitReached,
                    canCreateDiscussionThread: this.canUserCreateDiscussionThread(authUser, reactiveActivity),
                    discussionRestrictedUntilWeekStart: this.getDiscussionRestrictedUntilWeekStart(
                        authUser,
                        reactiveActivity
                    ),
                };

            case ACTIVITY_TYPES.FILE_SUBMISSION:
                return this.getFileSubmissionStatuses(sharedStatuses, reactiveActivity);

            case ACTIVITY_TYPES.QUIZ:
            case ACTIVITY_TYPES.SURVEY:
                return {
                    ...sharedStatuses,
                    maxReached: this.getTotalAttemptsRemainingForActivity(authUser, filterToUserId, activity) <= 0,
                    attemptsRemaining: this.getTotalAttemptsRemainingForActivity(authUser, filterToUserId, activity),
                    traditionalAttemptsRemaining: this.getTraditionalAttemptsRemainingForActivity(activity),
                    // Note: attemptInProgress, attemptId, checkedIn, canCheckInToQuiz, and userGrades
                    // must be passed from the component as they rely on component state/API calls
                    attemptInProgress: reactiveActivity?.attemptInProgress ?? false,
                    attemptId: reactiveActivity?.attemptId ?? null,
                    checkedIn: reactiveActivity?.checkedIn ?? false,
                    canCheckInToQuiz: reactiveActivity?.canCheckInToQuiz ?? false,
                    userGrades: reactiveActivity?.userGrades ?? [],
                };

            default:
                return sharedStatuses;
        }
    }

    /**
     * Get file submission statuses with reactive overrides
     * This method reads min/max files from the reactive activity prop to ensure reactivity
     *
     * @param {Object} sharedStatuses - Shared status flags
     * @param {Object} reactiveActivity - Reactive activity object from component prop
     * @returns {Object} File submission statuses with reactive values
     */
    getFileSubmissionStatuses(sharedStatuses, reactiveActivity = null) {
        const baseStatuses = {
            ...sharedStatuses,
            ...this.fileValidationStatus,
        };

        // If reactive activity prop is provided, use it for min/max files
        // This ensures reactivity when backend data updates
        if (reactiveActivity?.activity_content) {
            const minFiles = reactiveActivity.activity_content.min_files || 0;
            const maxFiles = reactiveActivity.activity_content.max_files || 0;

            return {
                ...baseStatuses,
                minFiles,
                maxFiles,
                minFilesNotMet: this.file_count < minFiles,
                maxFilesExceeded: maxFiles > 0 && this.file_count > maxFiles,
                showFileValidation: minFiles > 0 && !sharedStatuses.isCompleted,
            };
        }

        return baseStatuses;
    }

    /**
     * Update file count for file-submission activities
     */
    updateFileCount(count) {
        this.$update({
            file_count: count,
        });
    }

    /**
     * Sync activity data from Activity model
     * This ensures the ActivityStatus has access to latest activity data
     */
    static syncFromActivity(activity) {
        if (!activity?.id) {
            return null;
        }

        // Find or create ActivityStatus for this activity
        const existing = this.find(activity.id);

        const activityType = activity.activity_type || activity.activity_type_key;

        if (existing) {
            // Update existing
            this.update({
                where: activity.id,
                data: {
                    activity_type: activityType,
                },
            });
            return existing;
        }

        // Insert new
        return this.insert({
            data: {
                activity_id: activity.id,
                activity_type: activityType,
                file_count: 0,
            },
        });
    }
}
